import {Component, inject} from '@angular/core';
import {declarationResourceType} from "./configs/declaration-modal-config";
import {MatButton, MatMiniFabButton} from "@angular/material/button";
import {FormGroup} from "@angular/forms";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  DeclarationData,
  DeclarationDialogData,
  DeclarationFormField
} from "./types/declaration-modal.types";
import {DeclarationModalService} from "./services/declaration-modal.service";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatIcon} from "@angular/material/icon";
import {DeclarationFilesStateService} from "./services/declaration-files-state.service";
import {GetDataInModalParams} from "../../../types/product-card-modals.types";
import {declarationFormFields} from "./configs/declaration-form-fields";
import {defaultDeclarationData} from "./configs/default-declaration-data";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {DeclarationFormComponent} from "./components/declaration-form/declaration-form.component";
import {DeclarationFilesComponent} from "./components/declaration-files/declaration-files.component";

@Component({
  selector: 'app-declaration-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    MatMiniFabButton,
    DeclarationFormComponent,
    DeclarationFilesComponent
  ],
  templateUrl: './declaration-modal.component.html',
  styleUrl: './declaration-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class DeclarationModalComponent extends BaseModalComponent {
  public declarationDialogData = inject<DeclarationDialogData>(MAT_DIALOG_DATA);
  private declarationModalService: DeclarationModalService = inject(DeclarationModalService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);
  private declarationFilesStateService: DeclarationFilesStateService = inject(DeclarationFilesStateService);

  public readonly declarationResourceType = declarationResourceType;
  public declarationFormGroups: Array<FormGroup> = [];
  public declarationsData: Array<DeclarationData> = [];
  public isValid: boolean = false;
  public noFirstData: boolean = false;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.declarationResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<DeclarationFormField>(declarationFormFields);
    this.declarationFormGroups.push(formGroup);
    this.declarationsData.push(defaultDeclarationData);
    this.isValid = false;
  }

  public updateDeclarationData(data: DeclarationData, index: number): void {
    this.declarationsData[index] = data;
    this.declarationFormGroups.at(index)?.patchValue(data);
    this.isValid = !!data.id;
    this.noFirstData = !!this.declarationsData.length;
  }

  public deleteDeclaration(index: number): void {
    this.declarationFormGroups.splice(index, 1);
    this.declarationsData.splice(index, 1);
    this.isValid = true;
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    await this.deleteRecord(declarationResourceType.path, id)
      .then(() => this.removeDeclarationAtIndex(index, id));
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.declarationResourceType.key);
    await this.fetchDeclarationsData();
    this.isValid = !!this.declarationsData.at(0)?.id;
    this.noFirstData = !!this.declarationsData.at(0)?.id;
  }

  private async fetchDeclarationsData(): Promise<void> {
    const params: GetDataInModalParams = {
      path: declarationResourceType.path,
      queryParams: {
        technicalParametersId: Number(this.declarationDialogData.declarations.id)
      }
    };

    this.declarationsData = await this.declarationModalService.getDataInModal<DeclarationData>(params).catch(() => []);
    this.initForm();
  }

  private initForm(): void {
    if (!this.declarationsData.length) {
      this.handleAddNew();
      return;
    }

    this.declarationFormGroups = this.declarationsData.map(data => {
      const formGroup = this.baseModalService.getFormGroup<DeclarationFormField>(declarationFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });
  }

  private removeDeclarationAtIndex(index: number, id: number): void {
    this.declarationFormGroups.splice(index, 1);
    this.declarationsData.splice(index, 1);
    this.declarationFilesStateService.deleteDeclarationFilesState(id);

    if (!this.declarationsData.length) {
      this.noFirstData = false;
      this.handleAddNew();
    }
  }
}
